import { accessToken } from '@carvana/core-auth';
import { isSuccess } from '@carvana/merch-result';
import { LocationSource, TwoWeeks } from './constants';
import cookieClientToCoreCookieStore from './lib/cookieClientToCoreCookieClient';
import formatLocationData from './lib/formatLocationData';
import formatLocationOnError from './lib/formatLocationOnError';
import getLocationFromCookies from './lib/getLocationFromCookies';
import isServerSide from './lib/isServerSide';
import isValidZipCode from './lib/isValidZipCode';
import LoginCookie from './lib/LoginCookie';
import setCookies from './lib/setCookies';
import toError from './lib/toError';
import type {
  AddressResponse,
  ApiConfig,
  CookieClient,
  Fetcher,
  GeolocationApiClient,
  GoogleLocationAndMaxmindResponse,
  GoogleUtmLocationAndSource,
  LocationData,
  LocationResponse,
  ZipAndSource
} from './types';

export default class GeoLocationApi implements GeolocationApiClient {
  private readonly loginCookie: LoginCookie;

  constructor(
    private cookies: CookieClient,
    private internalConfig: ApiConfig,
    private fetcher: Fetcher,
    private fetcherWithAuth: Fetcher = fetcher,
    private skipSettingLoginCookies: boolean = false
  ) {
    this.loginCookie = new LoginCookie(cookies);
  }

  async getGeoLocation(): Promise<LocationData> {
    const location = await this.resolveGeoLocationData();

    this.setLocationCookies(location);

    return location;
  }

  async setGeoLocation({ source, zip }: ZipAndSource): Promise<LocationData> {
    const location = await this.resolveGeoLocationData({ source, zip });

    this.setLocationCookies(location);

    return location;
  }

  async getGoogleAndMaxmindGeolocation({
    utmLocationCode,
    source
  }: GoogleUtmLocationAndSource): Promise<{
    merchLocationData: LocationData;
    googleUtmLocationData?: LocationData;
  }> {
    const url = new URL(this.internalConfig.geolocationV2Api);
    url.searchParams.set('locationCode', utmLocationCode);
    url.searchParams.set('ipaddress', this.internalConfig.ipAddress ?? '');

    return this.getGoogleLocationAndMaxmindFromApi(url.toString(), source);
  }

  async getGeoLocationWithUtmOverride({
    zipAndSource,
    googleUtmLocationAndSource
  }: {
    zipAndSource?: ZipAndSource;
    googleUtmLocationAndSource?: GoogleUtmLocationAndSource;
  }): Promise<{
    locationToUse: LocationData;
    merchLocationData?: LocationData | null;
    googleUtmLocationData?: LocationData | null;
  }> {
    // Try Google UTM path
    if (googleUtmLocationAndSource) {
      const googleAndMaxMindLocation =
        await this.getGoogleAndMaxmindGeolocation(googleUtmLocationAndSource);

      // Prefer Google location if available
      if (googleAndMaxMindLocation.googleUtmLocationData) {
        this.setLocationCookies(googleAndMaxMindLocation.googleUtmLocationData);

        return {
          locationToUse: googleAndMaxMindLocation.googleUtmLocationData,
          googleUtmLocationData: googleAndMaxMindLocation.googleUtmLocationData,
          merchLocationData: googleAndMaxMindLocation.merchLocationData
        };
      }

      // Fallback to IP-based location from Google API (error block case in getGoogleLocationAndMaxmindFromApi)
      if (googleAndMaxMindLocation.merchLocationData) {
        this.setLocationCookies(googleAndMaxMindLocation.merchLocationData);

        return {
          locationToUse: googleAndMaxMindLocation.merchLocationData,
          googleUtmLocationData: undefined,
          merchLocationData: googleAndMaxMindLocation.merchLocationData
        };
      }

      throw new Error('Google UTM geolocation failed');
    }

    // Standard merch geolocation path
    const merchGeolocation = await this.resolveGeoLocationData(zipAndSource);
    this.setLocationCookies(merchGeolocation);

    return {
      locationToUse: merchGeolocation,
      googleUtmLocationData: undefined,
      merchLocationData: undefined
    };
  }

  private async resolveGeoLocationData(
    zipAndSource?: ZipAndSource
  ): Promise<LocationData> {
    if (zipAndSource) {
      // Has explicit zip/source - check account first, then use provided zip
      return (
        (await this.maybeGetAccountLocation()) ??
        (await this.setGeoLocationWithZipAndSource(
          zipAndSource.zip,
          zipAndSource.source
        ))
      );
    }

    // No zip/source - use getGeoLocation's fallback logic
    return (
      (await this.maybeGetAccountLocation()) ??
      this.getLocationFromCookies() ??
      (await this.getGeoLocationFromIpAddress())
    );
  }

  private async getAccountLocation(): Promise<string> {
    try {
      const { cvAccessToken, isLoggedIn, user } = accessToken({
        cookieStore: cookieClientToCoreCookieStore(this.cookies)
      });

      if (!cvAccessToken || !isLoggedIn || !user.user_id) {
        return '';
      }

      const addresses = await this.fetcherWithAuth<AddressResponse>(
        `${this.internalConfig.contactsApi}/${user.user_id}/addresses`
      );

      if (!isSuccess(addresses)) {
        if (this.internalConfig.onError) {
          this.internalConfig.onError(new Error(addresses.errorMessage));
        }

        return '';
      }

      const zipCodes = addresses.content.reduce<Record<string, string>>(
        (acc, address) => {
          if (address.kind && address.address) {
            acc[address.kind] = address.address.zipCode.zip5;
          }

          return acc;
        },
        {}
      );

      return (
        zipCodes.DeliveryZip ??
        zipCodes.VerifiedDelivery ??
        zipCodes.VerifiedHome ??
        ''
      );
    } catch (err) {
      if (this.internalConfig.onError) {
        this.internalConfig.onError(toError(err));
      }

      return '';
    }
  }

  // if the user logs in, and we haven't already set the MerchHasLoggedIn
  // cookie, then prioritize their account zip over the seo location
  private async maybeGetAccountLocation(): Promise<LocationData | undefined> {
    if (this.loginCookie.shouldRemoveCookie) {
      this.loginCookie.remove();

      return undefined;
    }

    if (!this.loginCookie.shouldSetCookie) {
      return undefined;
    }

    const zip = await this.getAccountLocation();

    if (zip && !isServerSide && !this.skipSettingLoginCookies) {
      this.loginCookie.set();
    }

    return zip
      ? this.setGeoLocationWithZipAndSource(zip, LocationSource.Account)
      : undefined;
  }

  private getLocationFromCookies(): LocationData | null {
    const location = getLocationFromCookies(this.cookies);

    return isValidZipCode(location?.zip ?? '') ? location : null;
  }

  private async getGeoLocationFromIpAddress(): Promise<LocationData> {
    const url = new URL(this.internalConfig.geolocationApi);
    url.searchParams.set('ipaddress', this.internalConfig.ipAddress ?? '');

    return this.getGeoLocationFromApi(url.toString(), LocationSource.Ipaddress);
  }

  private async setGeoLocationWithZipAndSource(
    zip: string,
    source: LocationSource
  ): Promise<LocationData> {
    const url = new URL(this.internalConfig.contextApi);
    url.searchParams.set('zip5', zip);

    return this.getGeoLocationFromApi(url.toString(), source);
  }

  private async getGeoLocationFromApi(
    url: string,
    source: LocationSource
  ): Promise<LocationData> {
    try {
      const fetchedLocation = await this.fetcher<LocationResponse>(url, {
        headers: this.internalConfig.browserCookieId
          ? { BrowserCookieId: this.internalConfig.browserCookieId }
          : undefined
      });

      return {
        ...formatLocationData(fetchedLocation),
        ipAddress: this.internalConfig.ipAddress ?? '',
        isFromCookies: false,
        source
      };
    } catch (err) {
      const location = formatLocationOnError(this.cookies);

      if (this.internalConfig.onError) {
        this.internalConfig.onError(toError(err));
      }

      return {
        ...location,
        ipAddress: location.ipAddress || this.internalConfig.ipAddress || '',
        isFromCookies: false,
        source: LocationSource.Default
      };
    }
  }

  private async getGoogleLocationAndMaxmindFromApi(
    url: string,
    source: LocationSource
  ): Promise<{
    merchLocationData: LocationData;
    googleUtmLocationData?: LocationData;
  }> {
    try {
      const fetchedLocation =
        await this.fetcher<GoogleLocationAndMaxmindResponse>(url, {
          headers: this.internalConfig.browserCookieId
            ? { BrowserCookieId: this.internalConfig.browserCookieId }
            : undefined
        });

      return {
        merchLocationData: {
          ...formatLocationData(fetchedLocation.ip),
          ipAddress: this.internalConfig.ipAddress ?? '',
          isFromCookies: false,
          source
        },
        googleUtmLocationData: {
          ...formatLocationData(fetchedLocation.google),
          ipAddress: this.internalConfig.ipAddress ?? '',
          isFromCookies: false,
          source
        }
      };
    } catch (err) {
      const location = formatLocationOnError(this.cookies);

      if (this.internalConfig.onError) {
        this.internalConfig.onError(toError(err));
      }

      return {
        merchLocationData: {
          ...location,
          ipAddress: location.ipAddress || this.internalConfig.ipAddress || '',
          isFromCookies: false,
          source: LocationSource.Default
        }
      };
    }
  }

  private setLocationCookies({
    ipAddress: _ipAddress,
    ...location
  }: LocationData): void {
    setCookies(this.cookies, location, {
      path: '/',
      'Max-Age': TwoWeeks
    });
  }
}
